import { createAsyncThunk } from '@reduxjs/toolkit'
import { createThunkName } from '../../../../utils/createThunkname'
import { AxiosService } from '../../../../services/api'
import {
  ApproveCommissionSummaryDto,
  CreateCommissionDto,
  DeleteCommissionDto,
  GetAllCommissionsQueryParams,
  GetAllCommissionsResponse,
  GetCommissionByDealerIdRequest,
  GetCommissionByDealerIdResponse,
  GetCommissionDealerInfoResponse,
  GetCommissionSummaryByDealerIdResponse,
  GetCommissionTransactionsByDealerIdRequest,
  GetCommissionTransactionsByDealerIdResponse,
  ImportCommissionRequest,
  SaveCommissionSummaryDto,
  UpdateCommissionDto,
  UpdateStatusCommissionTransactionDto
} from './types'
import { COMMISSION_URLS } from '../../../../configs/api'
import { REQUEST_OPTIONS } from '../../../../constants/request-options'
import { saveData } from '../../../../utils/downloadFile'

const PREFIX = 'commission'

export const CommissionService = {
  getAllCommissionsAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllCommissions'),
    async (queryParams: GetAllCommissionsQueryParams) => {
      try {
        const result = await AxiosService.get<GetAllCommissionsResponse>(COMMISSION_URLS.getAllCommissions, {
          params: queryParams
        })
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  exportCommissionsAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'exportCommissions'), async () => {
    try {
      const result = await AxiosService.get(COMMISSION_URLS.exportCommissions, {
        ...REQUEST_OPTIONS
      })
      saveData(result, result.request.getResponseHeader('Content-Disposition'))
    } catch (error) {
      throw error
    }
  }),
  getCommissionByDealerIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCommissionByDealerId'),
    async (request: GetCommissionByDealerIdRequest) => {
      try {
        const result = await AxiosService.get<GetCommissionByDealerIdResponse>(
          COMMISSION_URLS.getCommissionByDealerId(request.dealerId),
          {
            params: request.queryParams
          }
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  createCommissionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'createCommission'),
    async (dto: CreateCommissionDto) => {
      try {
        await AxiosService.post(COMMISSION_URLS.createCommission, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateCommissionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateCommission'),
    async (dto: UpdateCommissionDto) => {
      try {
        await AxiosService.put(COMMISSION_URLS.updateCommission, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  deleteCommissionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'deleteCommission'),
    async (dto: DeleteCommissionDto) => {
      try {
        await AxiosService.post(COMMISSION_URLS.deleteCommission, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  getCommissionTransactionsByDealerIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCommissionTransactionsByDealerId'),
    async (request: GetCommissionTransactionsByDealerIdRequest) => {
      try {
        const result = await AxiosService.get<GetCommissionTransactionsByDealerIdResponse>(
          COMMISSION_URLS.getCommissionTransactionsByDealerId(request.dealerId),
          {
            params: request.queryParams
          }
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  updateStatusCommissionTransactionsAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateStatusCommissionTransactions'),
    async (dto: UpdateStatusCommissionTransactionDto) => {
      try {
        await AxiosService.put(COMMISSION_URLS.updateStatusCommissionTransactions, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  getCommissionSummaryByDealerIdAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCommissionSummaryByDealerId'),
    async (dealerId: string) => {
      try {
        const result = await AxiosService.get<GetCommissionSummaryByDealerIdResponse>(
          COMMISSION_URLS.getCommissionSummaryByDealerId(dealerId)
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  saveCommissionSummaryAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'saveCommissionSummary'),
    async (dto: SaveCommissionSummaryDto) => {
      try {
        await AxiosService.put(COMMISSION_URLS.saveCommissionSummary, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  approveCommissionSummaryAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'approveCommissionSummary'),
    async (dto: ApproveCommissionSummaryDto) => {
      try {
        await AxiosService.put(COMMISSION_URLS.approveCommissionSummary, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  getCommissionDealerInfoAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCommissionDealerInfo'),
    async (dealerId: string) => {
      try {
        const result = await AxiosService.get<GetCommissionDealerInfoResponse>(
          COMMISSION_URLS.getCommissionDealerInfo(dealerId)
        )
        return result.data.commission
      } catch (error) {
        throw error
      }
    }
  ),
  importCommissionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'importCommission'),
    async (request: ImportCommissionRequest) => {
      try {
        const formData = new FormData()
        formData.append('file', request.file)
        await AxiosService.post(COMMISSION_URLS.importCommission, formData, {
          params: request.queryParams
        })
      } catch (error) {
        throw error
      }
    }
  ),
  downloadCommissionTemplateAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'downloadCommissionTemplate'),
    async () => {
      try {
        const result = await AxiosService.get(COMMISSION_URLS.downloadCommissionTemplate, {
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  )
}
